// Client-side copy of api/cloudflare/src/lib/totp.ts, used only for the key
// being typed into the form: whoever pastes it already holds it, and the site
// they are turning 2FA on in asks for a code before it saves the key. A saved
// key never comes back to the browser — its codes go through the server gate.

const B32 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

export function normalizeTotpSecret(input: string): string | null {
  let raw = input.trim();
  if (/^otpauth:\/\//i.test(raw)) {
    let url: URL;
    try { url = new URL(raw); } catch { return null; }
    const algo = url.searchParams.get('algorithm');
    const digits = url.searchParams.get('digits');
    const period = url.searchParams.get('period');
    if (algo && algo.toUpperCase() !== 'SHA1') return null;
    if (digits && digits !== '6') return null;
    if (period && period !== '30') return null;
    raw = url.searchParams.get('secret') ?? '';
  }
  const clean = raw.toUpperCase().replace(/[\s-]/g, '').replace(/=+$/, '');
  if (clean.length < 16 || !/^[A-Z2-7]+$/.test(clean)) return null;
  return clean;
}

export async function totpCode(seed: string, nowMs = Date.now()): Promise<string> {
  const bytes: number[] = [];
  let bits = 0;
  let value = 0;
  for (const ch of seed) {
    value = (value << 5) | B32.indexOf(ch);
    bits += 5;
    if (bits >= 8) { bytes.push((value >>> (bits - 8)) & 0xff); bits -= 8; }
  }
  const counter = new Uint8Array(8);
  new DataView(counter.buffer).setBigUint64(0, BigInt(Math.floor(nowMs / 30_000)));
  const key = await crypto.subtle.importKey(
    'raw', new Uint8Array(bytes), { name: 'HMAC', hash: 'SHA-1' }, false, ['sign'],
  );
  const h = new Uint8Array(await crypto.subtle.sign('HMAC', key, counter));
  const off = h[h.length - 1]! & 0x0f;
  const code = (new DataView(h.buffer).getUint32(off) & 0x7fffffff) % 1_000_000;
  return String(code).padStart(6, '0');
}

/** "005924" → "005 924", the way authenticator apps print it. */
export function formatTotp(code: string): string {
  return `${code.slice(0, 3)} ${code.slice(3)}`;
}
